import { Button } from "@/ui-components/ui/button";
import { Home } from "lucide-react";
import { BackgroundGradient } from "../common/BackgroundGradient";
import { StaticErrorHeader } from "./StaticErrorHeader";
import { isServerError } from "@/types/errors";

interface ServerErrorProps {
  error?: Error | null;
  resetError?: () => void;
}

export default function ServerError({ error, resetError }: ServerErrorProps) {
  // Extract the actual error code if it's a ServerError, otherwise default to 500
  const errorCode = isServerError(error) ? error.statusCode : 500;

  const handleGoHome = () => {
    // Clear the error state if resetError is provided
    if (resetError) {
      resetError();
    }
    // Use window.location instead of router since this component
    // may render outside the Router context (via ErrorBoundary in main.tsx)
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <StaticErrorHeader />

      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <BackgroundGradient />

        {/* Outer blur layer */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="w-[600px] h-[600px] bg-white/20 rounded-full blur-3xl"></div>
        </div>

        {/* Middle blur layer - using orange tint for 500 error */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="w-[400px] h-[400px] bg-[#EB7138]/10 rounded-full blur-2xl"></div>
        </div>

        <div className="text-center px-6 relative z-10">
          {/* Glass container with depth */}
          <div className="relative">
            {/* Shadow layer for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/10 rounded-3xl blur-xl transform translate-y-2"></div>

            {/* Main glass card */}
            <div className="relative backdrop-blur-xl bg-white/30 rounded-3xl border border-white/50 shadow-2xl px-6 py-10 sm:px-12 sm:py-16 md:px-20 md:py-20">
              {/* Inner glow layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-transparent rounded-3xl"></div>

              <div className="relative">
                {/* Error code display with subtle background */}
                <div className="mb-8 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-black/10 to-transparent rounded-full blur-2xl"></div>
                  </div>
                  <h1 className="text-6xl sm:text-8xl md:text-9xl font-bold text-black/15 relative tracking-tight">
                    {errorCode}
                  </h1>
                </div>

                {/* Message */}
                <h2 className="text-2xl md:text-3xl font-semibold text-black/75 mb-2">
                  Something broke in the sync.
                </h2>

                <p className="text-black/50 mb-10">
                  An error has occurred. Please try again later.
                </p>

                {/* Single action button */}
                <Button
                  onClick={handleGoHome}
                  className="bg-[#EB7138] hover:bg-[#EB7138]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>

                {/* Dev mode error details */}
                {import.meta.env.DEV && error && (
                  <div className="mt-4 p-4 bg-white/50 backdrop-blur-sm rounded-xl text-left max-w-lg mx-auto border border-white/60">
                    <p className="text-xs font-mono text-black/60 break-all line-clamp-2">
                      {error.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
